import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Input, Form, Select, Button } from 'antd';
import Currency from 'components/Currency';
import formikFieldToAntProps from '@lib/formik/formikFieldToAntProps';
import useWorkspace from 'store/redux/hooks/useWorkspace';
import DatePicker from 'components/DatePicker';

function CustomCostForm({ data, onSubmit, isLoading, editing = false }) {
  const { workspace } = useWorkspace();

  const handleFormSubmit = () => {
    onSubmit && onSubmit(form.values);
  };

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || '',
      type: data?.type || null,
      amount: data?.amount || '',
      currency: data?.currency || workspace.currency || 'USD',
      start_from: data?.start_from || '',
    },
    onSubmit: handleFormSubmit,
    validationSchema,
    validateOnChange: false,
  });

  useEffect(() => {
    if (data) {
      form.setValues({
        name: data.name || '',
        type: data.type || null,
        amount: data.amount || '',
        currency: data.currency || workspace.currency || 'USD',
        start_from: data?.start_from || '',
      });
    }
  }, [data, workspace.currency]);

  return (
    <Form layout='vertical' onFinish={handleFormSubmit}>
      <Form.Item
        rules={[{ required: true }]}
        label='Name'
        validateStatus={form.errors.name && form.touched.name ? 'error' : ''}
        help={form.touched.name && form.errors.name}
      >
        <Input
          onChange={(e) => {
            form.setFieldValue('name', e.target.value);
          }}
          onBlur={form.handleBlur}
          value={form.values.name}
          size='large'
          placeholder='Enter Name'
          name='name'
        />
      </Form.Item>
      <Form.Item
        label='Amount'
        rules={[{ required: true }]}
        hasFeedback
        {...formikFieldToAntProps(form.errors, form.touched, 'amount')}
      >
        <Input
          type='number'
          onChange={(e) => {
            form.setFieldValue('amount', e.target.value);
          }}
          onBlur={form.handleBlur}
          value={form.values.amount}
          size='large'
          placeholder='Enter Amount'
          name='amount'
          addonAfter={
            <Currency
              placeholder='Currency'
              className='w-24'
              value={form.values.currency}
              onChange={(value) => {
                form.setFieldValue('currency', value);
              }}
            />
          }
        />
      </Form.Item>
      <Form.Item
        label='Type'
        rules={[{ required: true }]}
        hasFeedback
        {...formikFieldToAntProps(form.errors, form.touched, 'type')}
      >
        <Select
          size='large'
          value={form.values.type}
          className='w-full'
          placeholder='Select a Type'
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'yearly', label: 'Yearly' },
          ]}
          onChange={(val) => {
            form.setFieldValue('type', val);
          }}
        />
      </Form.Item>
      <Form.Item
        rules={[{ required: true }]}
        label='Start Date'
        validateStatus={form.errors.start_from && form.touched.start_from ? 'error' : ''}
        help={form.touched.start_from && form.errors.start_from}
      >
        <DatePicker
          onChange={(value) => {
            form.setFieldValue('start_from', value);
          }}
          onBlur={form.handleBlur}
          value={form.values.start_from}
          size='large'
          placeholder='Select Date'
          name='start_from'
        />
      </Form.Item>
      <div className='flex-center mt-4'>
        <Button size='large' type='primary' loading={isLoading} onClick={form.handleSubmit}>
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </Form>
  );
}

const validationSchema = yup.object({
  name: yup.string('Enter Name').required('Name is required'),
  type: yup.string().nullable().required('Type is required'),
  amount: yup.number('Enter amount').required('The amount field is required'),
  start_from: yup.mixed('Select Date').required('The date field is required'),
});

export default CustomCostForm;
